import React from "react";
import get from "lodash/get";
import { useTranslation } from "react-i18next";
import NavigationComponent from "../calendar/components/NavigationComponent";
import { AthleteLinks } from "../../../parts/athlete-links";
import { AthleteSelect } from "../../../parts/inputs/athlete-select-new";
import SvgIcon from "../../../parts/SvgIcon";
import getAvatarUrl from "../../../utils/get-avatar-url";
import { ReportsBackHandler } from "../calendar/components/ReportsBackHandler";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { canViewAllOrganizationClients } from "../../../utils/organization-permissions-helpers";

const getMetricsBlocks = ({ organizationCoach, athlete, intensityMeasure, t }) => [
  {
    name: t("metrics.volumeIntensity"),
    href: organizationCoach
      ? `/athlete-portal/metrics/volume-intensity?athlete=${athlete}&organizationCoach=${organizationCoach}`
      : `/athlete-portal/metrics/volume-intensity?athlete=${athlete}`,
    iconSrc: "/img/new-icons/volume-intensity-ico.svg",
  },
  {
    name: t("metrics.baselineVolumeRelativeIntensity"),
    href: organizationCoach
      ? `/athlete-portal/metrics/baseline-volume-relative-intensity?athlete=${athlete}&organizationCoach=${organizationCoach}`
      : `/athlete-portal/metrics/baseline-volume-relative-intensity?athlete=${athlete}`,
    iconSrc: "/img/new-icons/baseline-vol-rel-intensity-ico.svg",
  },
  {
    name: t("metrics.overUnderBaselineVolume"),
    href: organizationCoach
      ? `/athlete-portal/metrics/over-under-baseline?athlete=${athlete}&organizationCoach=${organizationCoach}`
      : `/athlete-portal/metrics/over-under-baseline?athlete=${athlete}`,
    iconSrc: "/img/new-icons/over-under-baseline-vol-ico.svg",
  },
  {
    name: t("metrics.volumeSplitCoreLift"),
    href: `/athlete-portal/metrics/volume-split-core-lift?athlete=${athlete}`,
    iconSrc: "/img/new-icons/volume-split-ico.svg",
  },
  {
    name: t("metrics.personalRecordTable"),
    href: `/athlete-portal/metrics/record-table?athlete=${athlete}`,
    iconSrc: "/img/new-icons/personal-record-ico.svg",
  },
  {
    name: t("metrics.numberOfLifts"),
    href: organizationCoach
      ? `/athlete-portal/metrics/number-of-lifts?athlete=${athlete}&organizationCoach=${organizationCoach}`
      : `/athlete-portal/metrics/number-of-lifts?athlete=${athlete}`,
    iconSrc: "/img/new-icons/number-of-lifts-ico.svg",
  },
  {
    name: t("metrics.primarySecondaryVolumeSplit"),
    href: organizationCoach
      ? `/athlete-portal/metrics/core-accessory-volume-split?athlete=${athlete}&organizationCoach=${organizationCoach}`
      : `/athlete-portal/metrics/core-accessory-volume-split?athlete=${athlete}`,
    iconSrc: "/img/new-icons/primary-secondary-vol-split-ico.svg",
  },
  {
    name: t("metrics.repMaxEstimator"),
    href: `/athlete-portal/metrics/rep-max-estimator?athlete=${athlete}`,
    iconSrc: "/img/new-icons/rep-max-ico.svg",
  },
  {
    name: t("metrics.customTables", { measure: intensityMeasure }),
    href: `/athlete-portal/metrics/rpe-table?athlete=${athlete}`,
    iconSrc: "/img/new-icons/rpe-custom-ico.svg",
  },
  {
    name: t("metrics.customMetricReports"),
    href: `/athlete-portal/metrics/custom?athlete=${athlete}`,
    iconSrc: "/img/new-icons/custom-metrics-ico.svg",
    className: "custom-metrics",
  },
];

function View(props) {
  const { t } = useTranslation("athlete-portal");
  const { locals } = useRillContext();

  const { activeUserToRequest, athlete: athleteForLinks } = locals;
  const athlete = get(locals, "query.athlete");
  const query = get(locals, "query");
  const pathname = get(locals, "pathname");
  const intensityMeasure =
    get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
      ? "RPE"
      : "RiR";
  const organizationCoach = query.organizationCoach || undefined;
  const metricsBlocks = getMetricsBlocks({
    organizationCoach,
    athlete,
    intensityMeasure,
    t,
  });
  const appMainColor = get(
    locals,
    "activeUser.customBranding.appMainColor",
    "var(--white-05)"
  );

  return (
    <main role="reports-wrapper">
      <div className="athlete-section-header only-show-mobile">
        <AthleteLinks
          athlete={athleteForLinks}
          pathName={pathname}
          classNames={"only-show-mobile"}
          query={query}
        />
        <div className="athlete-select dropdown-athlete">
          <div
            className="avatar"
            style={{
              backgroundImage: `url(${getAvatarUrl(locals?.athlete?._id)})`,
            }}
          />
          <AthleteSelect locals={locals} />
        </div>
      </div>
      <NavigationComponent
        query={query}
        pathName={pathname}
        textKey="nav.calendarDayView"
        classNames="mobile-hidden"
      />
      <article role="report-content" className="metrics-dashboard">
        <header>
          <h2 className="mobile-hidden">{t("metrics.strengthMetrics")}</h2>
          <ReportsBackHandler title={t("metrics.strengthMetrics")} locals={locals} />
        </header>
        <section className="Metrics-Blocks">
          {metricsBlocks.map(({ href, iconSrc, name, className }) => {
            return (
              <div
                key={iconSrc}
                className={`block${className ? ` ${className}` : ""}`}
              >
                <a href={href}>
                  <div>
                    <SvgIcon
                      src={iconSrc}
                      className="metrics-icon"
                      style={{ backgroundColor: appMainColor }}
                    />
                    <span className="name">{name}</span>
                  </div>
                  <picture className="only-show-mobile">
                    <img src="/img/new-icons/arrow-right.svg" />
                  </picture>
                </a>
              </div>
            );
          })}
        </section>
      </article>
    </main>
  );
}

const Metrics = (app) => {
  app.get("/", async ({ res, locals, load }) => {
    const { activeUser, allAthletesFromOrganization, organizationRoleName } =
      locals;
    await Promise.all([
      ...[
        activeUser.organizationId &&
        canViewAllOrganizationClients(organizationRoleName) &&
        !allAthletesFromOrganization
          ? await load("allAthletesFromOrganization")
          : [],
      ],
    ]);

    res.body = <View />;
  });
};

export default Metrics;
